import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { useAppSelector } from "hooks/redux";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";
import { WEBSITE_BUILDER_PORTAL } from "../../../../WebsiteBuilderEditor";
import { isWebsiteBuilderImageAllowedFileSubtype, isWebsiteBuilderVideoAllowedFileSubtype } from "../../../../utility/websiteBuilderAllowedMedia";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faVideo } from "@fortawesome/pro-regular-svg-icons";

type TWebsiteBuilderSelectMediaType = "image" | "video" | "both";

interface IWebsiteBuilderSelectMediaProps {
  type: TWebsiteBuilderSelectMediaType;
  mediaId: number;
  isOpen: boolean;
  closeModal: () => void;
  handleSelectMedia: (media: IWebsiteBuilderMediaItem) => void;
}

export function WebsiteBuilderSelectMedia(props: IWebsiteBuilderSelectMediaProps) {
  const { type, mediaId, isOpen, closeModal, handleSelectMedia } = props;

  const { t } = useTranslation();

  const media = useAppSelector(store => store.websiteBuilderStore.editor.media);
  const stockMedia = useAppSelector(store => store.websiteBuilderStore.editor.stockMedia);

  function isAllowedMediaItem(mediaItem: IWebsiteBuilderMediaItem) {
    const isImage = isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype);
    const isVideo = isWebsiteBuilderVideoAllowedFileSubtype(mediaItem.subtype);

    if (type === "image") {
      return isImage;
    } else if (type === "video") {
      return isVideo;
    } else {
      return isImage || isVideo;
    }
  }

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeModal();
      }
    },
    [closeModal],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.addEventListener("keydown", handleKeydown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeydown, { capture: true });
  }, [isOpen, handleKeydown]);

  function getTitles() {
    if (type === "image") {
      return {
        mediaTitle: t("Uploaded Images"),
        stockMediaTitle: t("Stock images"),
        mediaEmptyMessage: t("No images available"),
        sheetTitle: t("Select image"),
      };
    } else if (type === "video") {
      return {
        mediaTitle: t("Videos"),
        stockMediaTitle: t("Stock videos"),
        mediaEmptyMessage: t("No videos available"),
        sheetTitle: t("Select video"),
      };
    } else {
      return {
        mediaTitle: t("Media"),
        stockMediaTitle: t("Stock media"),
        mediaEmptyMessage: t("No media available"),
        sheetTitle: t("Select media"),
      };
    }
  }

  const { mediaTitle, stockMediaTitle, mediaEmptyMessage, sheetTitle } = getTitles();

  const mediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return media
      ?.filter(mediaItem =>
        isAllowedMediaItem(mediaItem) &&
        mediaItem.id !== mediaId &&
        mediaItem.archived_at == null,
      ) ?? [];
  }, [media, type, mediaId]);

  const stockMediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return stockMedia
      ?.filter(mediaItem =>
        isAllowedMediaItem(mediaItem) &&
        mediaItem.id !== mediaId,
      ) ?? [];
  }, [stockMedia, type, mediaId]);

  function renderMediaTypeIcon(mediaItem: IWebsiteBuilderMediaItem) {
    if (type !== "both") {
      return null;
    }

    if (isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype)) {
      return <FontAwesomeIcon className="select-media-option-type-icon" icon={faImage} />;
    } else if (isWebsiteBuilderVideoAllowedFileSubtype(mediaItem.subtype)) {
      return <FontAwesomeIcon className="select-media-option-type-icon" icon={faVideo} />;
    } else {
      return null;
    }
  }

  function renderMediaPreview(mediaItem: IWebsiteBuilderMediaItem) {
    if (isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype)) {
      return <img src={mediaItem.source} />;
    } else if (isWebsiteBuilderVideoAllowedFileSubtype(mediaItem.subtype)) {
      return (
        <video
          src={mediaItem.source}
          preload="metadata"
          muted
          playsInline
          disablePictureInPicture
        />
      );
    } else {
      return null;
    }
  }

  function renderMediaOptions(options: IWebsiteBuilderMediaItem[]) {
    return options.map(mediaItem =>
      <div
        key={mediaItem.id}
        className={classNames("select-media-option", {
          "select-media-option-both": type === "both",
        })}
        onClick={() => handleSelectMedia(mediaItem)}
      >
        {renderMediaPreview(mediaItem)}
        {renderMediaTypeIcon(mediaItem)}
      </div>,
    );
  }

  const noMediaOptionsAvailable = mediaOptions.length === 0 && stockMediaOptions.length === 0;

  function renderMediaSection(title: string, options: IWebsiteBuilderMediaItem[]) {
    if (options.length === 0) {
      return null;
    }

    return (
      <div className="select-media-section">
        <div>{title}</div>
        <div className="select-media-options">
          {renderMediaOptions(options)}
        </div>
      </div>
    );
  }

  return (
    <Portal isMounted={isOpen} parentId={WEBSITE_BUILDER_PORTAL}>
      <Sheet
        title={sheetTitle}
        open={isOpen}
        size="small"
        closable
        onCancel={closeModal}
      >
        <div className="website-builder-select-media">
          {noMediaOptionsAvailable ? (
            <div className="select-media-empty-message">{mediaEmptyMessage}</div>
          ) : (
            <>
              {renderMediaSection(mediaTitle, mediaOptions)}
              {renderMediaSection(stockMediaTitle, stockMediaOptions)}
            </>
          )}
        </div>
      </Sheet>
    </Portal>
  );
}
